/**
 * ApiClient — HTTP client for the CRM backend API.
 *
 * Access JWT lives in memory only. Refresh JWT is an httpOnly cookie
 * (set by /auth/login|/refresh|/microsoft; cleared by /auth/logout).
 * Use same-origin requests (Vite /api proxy or reverse proxy) so cookies work.
 *
 * Features:
 * - Standardized error envelope parsing
 * - Retry on network errors AND 5xx server errors
 * - Single-flight token refresh on 401
 * - Connection status events
 * - Rate limit (429) handling
 */

/** Empty = same-origin (Vite proxy / nginx). Prefer over cross-origin localhost:8000 for cookies. */
const DEFAULT_API_BASE = '';
export const API_MAX_PER_PAGE = 100;

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = [1000, 2000, 4000];
let _isOffline = false;
/** In-memory access token — never written to Web Storage. */
let _accessToken = null;

function _dispatchConnectionEvent(online) {
    _isOffline = !online;
    window.dispatchEvent(new CustomEvent('connection:changed', { detail: { online } }));
}

function _isNetworkError(error) {
    return error instanceof TypeError || error.name === 'AbortError' || error.name === 'TypeError';
}

function _isServerError(status) {
    return status >= 500 && status < 600;
}

function _sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Parse an API error response into a structured object.
 * Handles multiple response shapes:
 * - Standard envelope: { error: { code, message, details } }
 * - FastAPI default: { detail: "..." } or { detail: [...] }
 * - Plain: { message: "..." }
 * - Non-JSON: falls back to status text
 */
function _parseApiError(res, json) {
    const status = res.status;
    const statusText = res.statusText;

    // Standard envelope format: { error: { code, message, details } }
    if (json?.error) {
        return {
            code: json.error.code || 'UNKNOWN_ERROR',
            message: json.error.message || json.error.code || `API ${status}: ${statusText}`,
            details: json.error.details || {},
            statusCode: status,
        };
    }

    // FastAPI default validation error format: { detail: [...] }
    if (json?.detail && Array.isArray(json.detail)) {
        const messages = json.detail.map(d => {
            const field = d.loc ? d.loc.slice(1).join('.') : '';
            return field ? `${field}: ${d.msg}` : d.msg;
        });
        return {
            code: 'VALIDATION_ERROR',
            message: messages.join('; ') || 'Request validation failed',
            details: { errors: json.detail },
            statusCode: status,
        };
    }

    // FastAPI single detail string: { detail: "..." }
    if (json?.detail && typeof json.detail === 'string') {
        return {
            code: 'API_ERROR',
            message: json.detail,
            details: {},
            statusCode: status,
        };
    }

    // Plain message format: { message: "..." }
    if (json?.message) {
        return {
            code: 'API_ERROR',
            message: json.message,
            details: {},
            statusCode: status,
        };
    }

    // Fallback to status text
    return {
        code: 'HTTP_ERROR',
        message: `API ${status}: ${statusText}`,
        details: {},
        statusCode: status,
    };
}

export class ApiClient {
    static getBaseUrl() {
        if (typeof window !== 'undefined' && window.__CRM_CONFIG__ && 'API_BASE_URL' in window.__CRM_CONFIG__) {
            return window.__CRM_CONFIG__.API_BASE_URL || '';
        }
        const stored = localStorage.getItem('crm_api_base');
        if (stored) return stored;
        return DEFAULT_API_BASE;
    }

    static getToken() {
        return _accessToken || null;
    }

    static setAccessToken(token) {
        _accessToken = token || null;
    }

    static clearAccessToken() {
        _accessToken = null;
    }

    static clearSession() {
        this.clearAccessToken();
        try { sessionStorage.removeItem('quickcrm_session'); } catch {}
        try { localStorage.removeItem('quickcrm_session_local'); } catch {}
        window.dispatchEvent(new CustomEvent('authChanged', { detail: { user: null } }));
    }

    static async request(method, path, { body = null, params = null, headers = null, retryOnUnauthorized = true, timeout = 15000, _retryCount = 0, etag = null, rawResponse = false } = {}) {
        const authToken = this.getToken();

        const finalHeaders = {
            Accept: 'application/json',
            ...(headers || {})
        };

        if (authToken) finalHeaders.Authorization = `Bearer ${authToken}`;
        if (etag) finalHeaders['If-None-Match'] = etag;

        const options = { method, headers: finalHeaders, credentials: 'include' };
        if (body !== null) {
            const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
            if (!isFormData && !('Content-Type' in finalHeaders)) {
                finalHeaders['Content-Type'] = 'application/json';
            }
            if (finalHeaders['Content-Type']?.includes('application/json')) {
                options.body = JSON.stringify(body);
            } else {
                options.body = body;
            }
        }

        const url = `${this.buildUrl(path)}${this.buildQueryString(this.normalizeListParams(params))}`;
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);
        options.signal = controller.signal;

        try {
            const res = await fetch(url, options);
            clearTimeout(timeoutId);

            if (res.status === 401 && retryOnUnauthorized && path !== '/api/v1/auth/refresh') {
                const refreshed = await this.tryRefreshToken();
                if (refreshed) {
                    return this.request(method, path, { body, params, headers, retryOnUnauthorized: false, timeout, _retryCount, etag, rawResponse });
                }
                this.clearSession();
            }

            if (res.status === 304) {
                const result = {
                    notModified: true,
                    etag: res.headers.get('ETag') || etag || null,
                    data: null,
                    meta: null,
                };
                if (rawResponse) return result;
                const err = new Error('Not Modified');
                err.code = 'NOT_MODIFIED';
                err.statusCode = 304;
                err.etag = result.etag;
                throw err;
            }

            const isNoContent = res.status === 204;
            const json = isNoContent ? null : await res.json().catch(() => ({}));

            if (!res.ok) {
                // Build a structured error from the response
                const parsedError = _parseApiError(res, json);
                const error = new Error(parsedError.message);
                error.code = parsedError.code;
                error.details = parsedError.details;
                error.statusCode = parsedError.statusCode;
                throw error;
            }

            if (_isOffline) _dispatchConnectionEvent(true);
            if (isNoContent) {
                return rawResponse ? { notModified: false, etag: res.headers.get('ETag'), data: null } : null;
            }

            let data = json;
            if (json && typeof json === 'object' && 'data' in json) {
                if (json.error) {
                    const parsedError = _parseApiError(res, json);
                    const error = new Error(parsedError.message);
                    error.code = parsedError.code;
                    error.details = parsedError.details;
                    error.statusCode = parsedError.statusCode;
                    throw error;
                }
                data = json.data;
            }

            if (rawResponse) {
                return {
                    notModified: false,
                    etag: res.headers.get('ETag'),
                    data,
                    meta: json && typeof json === 'object' ? (json.meta ?? null) : null,
                };
            }
            return data;
        } catch (error) {
            clearTimeout(timeoutId);

            if (error?.code === 'NOT_MODIFIED') throw error;

            const isNetworkErr = _isNetworkError(error);
            const isServerErr = error.statusCode ? _isServerError(error.statusCode) : false;
            const noRetryCodes = new Set([
                'DB_CONNECTION_LIMIT_REACHED',
                'RATE_LIMITED',
                'VERSION_CONFLICT',
                'NOT_MODIFIED',
            ]);
            const shouldRetry = (isNetworkErr || isServerErr)
                && !noRetryCodes.has(error.code)
                && error.statusCode !== 429
                && error.statusCode !== 304
                && _retryCount < MAX_RETRIES;

            // Retry on network errors OR 5xx server errors (up to MAX_RETRIES)
            if (shouldRetry) {
                const delay = RETRY_DELAY_MS[_retryCount] || RETRY_DELAY_MS[RETRY_DELAY_MS.length - 1];
                console.warn(`API request failed (attempt ${_retryCount + 1}/${MAX_RETRIES + 1}), retrying in ${delay}ms: ${path} (${isNetworkErr ? 'network' : isServerErr ? 'server ' + error.statusCode : 'other'} error)`);
                await _sleep(delay);
                return this.request(method, path, { body, params, headers, retryOnUnauthorized, timeout, _retryCount: _retryCount + 1, etag, rawResponse });
            }

            if (isNetworkErr && !_isOffline) _dispatchConnectionEvent(false);

            if (error.name === 'AbortError') {
                const timeoutError = new Error(`Request timeout for ${path} after ${timeout}ms`);
                timeoutError.code = 'TIMEOUT';
                timeoutError.statusCode = 408;
                throw timeoutError;
            }

            // Ensure all thrown errors have at least a code property
            if (!error.code) {
                error.code = 'UNKNOWN_ERROR';
                error.statusCode = error.statusCode || 0;
                error.details = error.details || {};
            }

            throw error;
        }
    }

    static buildUrl(path) {
        const base = String(this.getBaseUrl() || '').replace(/\/+$/, '');
        const p = String(path || '');
        if (!base) {
            return p.startsWith('/') ? p : `/${p}`;
        }
        const normalizedPath = p.startsWith('/') ? p : `/${p}`;
        return `${base}${normalizedPath}`;
    }

    static buildQueryString(params) {
        if (!params) return '';
        const cleaned = this.omitEmptyParams(params);

        if (Object.keys(cleaned).length === 0) return '';

        const parts = [];
        for (const [key, value] of Object.entries(cleaned)) {
            if (Array.isArray(value)) {
                value.forEach((item) => {
                    if (item !== undefined && item !== null && item !== '') {
                        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(item)}`);
                    }
                });
            } else {
                parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
            }
        }
        return parts.length ? `?${parts.join('&')}` : '';
    }

    static omitEmptyParams(params) {
        if (!params) return {};
        return Object.fromEntries(
            Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
        );
    }

    static normalizeListParams(params) {
        const cleaned = this.omitEmptyParams(params);
        if (cleaned.per_page != null) {
            cleaned.per_page = Math.min(API_MAX_PER_PAGE, Math.max(1, Number(cleaned.per_page) || 50));
        }
        return cleaned;
    }

    static async tryRefreshToken() {
        // Single-flight: concurrent 401s (e.g. the parallel Kanban requests) share
        // one refresh round-trip instead of firing multiple /auth/refresh POSTs.
        if (!this._refreshInFlight) {
            this._refreshInFlight = this._refreshAccessToken().finally(() => {
                this._refreshInFlight = null;
            });
        }
        return this._refreshInFlight;
    }

    static async _refreshAccessToken() {
        try {
            const res = await fetch(`${this.buildUrl('/api/v1/auth/refresh')}`, {
                method: 'POST',
                headers: { Accept: 'application/json' },
                credentials: 'include',
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok || json?.error || !json?.data?.access_token) {
                this.clearAccessToken();
                return false;
            }
            this.setAccessToken(json.data.access_token);
            return true;
        } catch {
            return false;
        }
    }

    static async logoutServer() {
        try {
            await fetch(`${this.buildUrl('/api/v1/auth/logout')}`, {
                method: 'POST',
                headers: { Accept: 'application/json' },
                credentials: 'include',
            });
        } catch {
            /* ignore network errors on logout */
        }
    }

    static setBaseUrl(url) {
        const trimmed = String(url || '').trim().replace(/\/+$/, '');
        if (trimmed) localStorage.setItem('crm_api_base', trimmed);
        else localStorage.removeItem('crm_api_base');
    }

    static get(path, params = null, headers = null) { return this.request('GET', path, { params, headers }); }
    static getWithMeta(path, { params = null, etag = null, headers = null } = {}) {
        return this.request('GET', path, { params, headers, etag, rawResponse: true });
    }
    static post(path, body, headers = null) { return this.request('POST', path, { body, headers }); }
    static put(path, body, headers = null) { return this.request('PUT', path, { body, headers }); }
    static patch(path, body, headers = null) { return this.request('PATCH', path, { body, headers }); }
    static delete(path, body = null, headers = null) { return this.request('DELETE', path, { body, headers }); }

    /** Fetch a binary blob (e.g. avatar) with auth — returns Blob or null on 404 */
    static async fetchBlob(path, { etag = null } = {}) {
        const token = this.getToken();
        const headers = {};
        if (token) headers.Authorization = `Bearer ${token}`;
        if (etag) headers['If-None-Match'] = etag;
        const url = this.buildUrl(path);
        const doFetch = async (retry = true) => {
            const res = await fetch(url, { method: 'GET', headers, credentials: 'include' });
            if (res.status === 304) {
                const err = new Error('Not Modified');
                err.code = 'NOT_MODIFIED';
                err.statusCode = 304;
                err.etag = res.headers.get('ETag');
                throw err;
            }
            if (res.status === 401 && retry && path !== '/api/v1/auth/refresh') {
                const refreshed = await this.tryRefreshToken();
                if (refreshed) {
                    const newToken = this.getToken();
                    if (newToken) headers.Authorization = `Bearer ${newToken}`;
                    return doFetch(false);
                }
            }
            if (res.status === 404) return null;
            if (!res.ok) {
                const json = await res.json().catch(() => ({}));
                const parsed = _parseApiError(res, json);
                const e = new Error(parsed.message);
                e.code = parsed.code;
                e.statusCode = parsed.statusCode;
                throw e;
            }
            const blob = await res.blob();
            return { blob, etag: res.headers.get('ETag'), contentType: res.headers.get('Content-Type') };
        };
        return doFetch(true);
    }
}